import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Brain,
  TrendingUp,
  AlertTriangle,
  CheckCircle2,
  BookOpen,
  Link2,
  Loader2,
  Sparkles,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import api from "@/lib/api";

/**
 * CarePlanAiAnalysisPanel — análise IA holística do Plano de Cuidados.
 *
 * Renderiza:
 *  - Botão "Analisar com IA" (+ "Ver última análise" quando já corrida antes)
 *  - Modal com Overall + Coherence/Completeness + Alertas críticos + Lacunas
 *    + Incoerências entre blocos + Sugestões por bloco (com apply/apply all)
 *    + Insights do Manual do Cuidador + Próximos passos recomendados
 *
 * Backend endpoints:
 *  - POST /api/care-plans/{lead_id}/analyze
 *  - GET  /api/care-plans/{lead_id}/analysis
 *  - POST /api/care-plans/{lead_id}/analysis/apply
 */

const BLOCK_LABELS_PT = {
  service_identification: "Identificação do serviço",
  critical_summary: "Resumo crítico",
  person_profile: "Perfil da pessoa",
  functional_assessment: "Avaliação funcional",
  risks: "Riscos identificados",
  needs_objectives: "Necessidades & Objectivos",
  client_preferences: "Preferências do cliente",
  weekly_plan: "Plano semanal",
  caregiver_daily_tasks: "Tarefas diárias do AF",
  care_delivery: "Prestação de cuidados",
  equipment: "Equipamentos",
  team: "Equipa dedicada",
  rotinas: "Rotinas",
};
const blockLabel = (k) => BLOCK_LABELS_PT[k] || k;

function SeverityBadge({ severity }) {
  const map = {
    high: "bg-[hsl(var(--warning-bg))] text-[hsl(var(--danger))]",
    medium: "bg-[hsl(var(--warning-bg))] text-[hsl(var(--warning))]",
    low: "bg-muted text-muted-foreground",
  };
  const cls = map[severity] || map.medium;
  return (
    <span className={`text-[10px] uppercase tracking-[0.14em] font-semibold px-2 py-0.5 rounded-full ${cls}`}>
      {severity || "med"}
    </span>
  );
}

function ScoreCard({ label, value }) {
  if (typeof value !== "number") return null;
  const color =
    value >= 70 ? "text-[#059669]" : value >= 40 ? "text-[hsl(var(--warning))]" : "text-[hsl(var(--danger))]";
  return (
    <div className="text-center bg-card border border-border rounded-xl p-3">
      <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">{label}</div>
      <div className={`font-display text-3xl mt-1 ${color}`}>{value}%</div>
    </div>
  );
}

export default function CarePlanAiAnalysisPanel({
  leadId,
  currentVersion,
  onApplied,
  canEdit = true,
}) {
  const isFrozen = !!currentVersion?.is_frozen;
  const [analysis, setAnalysis] = useState(null);
  const [applied, setApplied] = useState({}); // key = "block:field?" → true
  const [open, setOpen] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [applyingKey, setApplyingKey] = useState(null);
  const [applyingAll, setApplyingAll] = useState(false);

  // Rehydrate from persisted current_version.ai_analysis
  useEffect(() => {
    const persisted = currentVersion?.ai_analysis;
    if (persisted) {
      setAnalysis(persisted);
      const map = {};
      for (const rec of persisted.applied_suggestions || []) {
        const k = rec.field ? `${rec.block}:${rec.field}` : rec.block;
        map[k] = true;
      }
      setApplied(map);
    } else {
      setAnalysis(null);
      setApplied({});
    }
  }, [currentVersion?.id, currentVersion?.ai_analysis]);

  const runAnalysis = async () => {
    setAnalyzing(true);
    try {
      const { data } = await api.post(`/care-plans/${leadId}/analyze`);
      setAnalysis(data);
      const map = {};
      for (const rec of data.applied_suggestions || []) {
        const k = rec.field ? `${rec.block}:${rec.field}` : rec.block;
        map[k] = true;
      }
      setApplied(map);
      setOpen(true);
      toast.success("Análise IA do plano concluída");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Falha na análise IA");
    } finally {
      setAnalyzing(false);
    }
  };

  const applyOne = async (item) => {
    const key = item.field ? `${item.block}:${item.field}` : item.block;
    setApplyingKey(key);
    try {
      const { data } = await api.post(`/care-plans/${leadId}/analysis/apply`, {
        items: [
          {
            block: item.block,
            field: item.field || null,
            text: item.suggested_text,
          },
        ],
      });
      setApplied((prev) => ({ ...prev, [key]: true }));
      toast.success(`Aplicada em "${blockLabel(item.block)}"`);
      if (onApplied && data.current_version) onApplied(data.current_version);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao aplicar sugestão");
    } finally {
      setApplyingKey(null);
    }
  };

  const applyAll = async () => {
    const pending = (analysis?.suggestions || []).filter((s) => {
      const key = s.field ? `${s.block}:${s.field}` : s.block;
      return !applied[key];
    });
    if (pending.length === 0) return;
    if (!window.confirm(`Aplicar ${pending.length} sugestões IA a todos os blocos afectados?`)) return;
    setApplyingAll(true);
    try {
      const { data } = await api.post(`/care-plans/${leadId}/analysis/apply`, {
        items: pending.map((s) => ({
          block: s.block,
          field: s.field || null,
          text: s.suggested_text,
        })),
      });
      const newApplied = { ...applied };
      for (const s of pending) {
        const key = s.field ? `${s.block}:${s.field}` : s.block;
        newApplied[key] = true;
      }
      setApplied(newApplied);
      toast.success(`Aplicadas ${pending.length} sugestões`);
      if (onApplied && data.current_version) onApplied(data.current_version);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao aplicar sugestões");
    } finally {
      setApplyingAll(false);
    }
  };

  // Botão de trigger — só aparece em rascunho (não congelada)
  const notApplied = (analysis?.suggestions || []).filter((s) => {
    const key = s.field ? `${s.block}:${s.field}` : s.block;
    return !applied[key];
  });

  return (
    <>
      <div
        className="flex items-center justify-between gap-3 flex-wrap mt-6 pt-6 border-t border-border"
        data-testid="cp-ai-analysis-bar"
      >
        <div className="flex flex-col gap-0.5">
          <div className="text-[11px] uppercase tracking-[0.18em] text-[hsl(var(--warning))] font-semibold flex items-center gap-1.5">
            <Sparkles className="w-3.5 h-3.5" />
            Análise IA do Plano de Cuidados
          </div>
          <div className="text-[13px] text-muted-foreground">
            {isFrozen
              ? "A versão está congelada — reabra para poder analisar."
              : "Detecta lacunas, incoerências entre blocos e sugere melhorias com base no Manual do Cuidador."}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {analysis && (
            <button
              type="button"
              onClick={() => setOpen(true)}
              data-testid="cp-analysis-view-btn"
              className="text-[11px] uppercase tracking-[0.14em] font-semibold px-3 py-1.5 rounded-full border border-primary text-primary hover:bg-primary/5 transition-colors"
            >
              Ver última análise
            </button>
          )}
          {/* 28/02/2026 — botão Analisar com IA só é renderizado para
              utilizadores com permissão de edição (canEdit=true). Leitores
              (gestor/consultor) não veem o CTA e assim não disparam
              chamadas 403 → toast forbidden. */}
          {canEdit && (
            <button
              type="button"
              onClick={runAnalysis}
              disabled={analyzing || isFrozen}
              data-testid="cp-analysis-run-btn"
              className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.14em] font-semibold px-3.5 py-1.5 rounded-full bg-primary text-primary-foreground hover:bg-primary transition-colors disabled:bg-[#94A3B8] disabled:cursor-not-allowed"
            >
              {analyzing ? (
                <>
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  A analisar…
                </>
              ) : (
                <>
                  <Brain className="w-3.5 h-3.5" />
                  Analisar com IA
                </>
              )}
            </button>
          )}
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          className="max-w-3xl max-h-[85vh] overflow-y-auto"
          data-testid="cp-analysis-dialog"
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-display text-2xl">
              <Brain className="w-6 h-6 text-primary" />
              Análise IA do Plano de Cuidados
            </DialogTitle>
            <DialogDescription className="text-muted-foreground">
              Revisão holística — completude, coerência entre blocos e boas práticas
              do Manual do Cuidador.
            </DialogDescription>
          </DialogHeader>

          {!analysis ? (
            <div className="py-10 text-center text-muted-foreground">
              Sem análise disponível. Clica em "Analisar com IA" para começar.
            </div>
          ) : (
            <div className="space-y-5 text-sm text-foreground" data-testid="cp-analysis-content">
              {/* Overall + scores */}
              <div className="grid grid-cols-1 md:grid-cols-[1fr_120px_120px] gap-3 items-start">
                <div className="bg-[hsl(var(--info-bg))] border border-primary/20 rounded-xl p-4">
                  <div className="text-[10px] uppercase tracking-[0.2em] text-primary font-semibold mb-1">
                    Avaliação global
                  </div>
                  <p className="leading-relaxed text-[13px]">
                    {analysis.overall_assessment || "—"}
                  </p>
                </div>
                <ScoreCard label="Completude" value={analysis.completeness_score} />
                <ScoreCard label="Coerência" value={analysis.coherence_score} />
              </div>

              {/* Critical alerts */}
              {analysis.critical_alerts?.length > 0 && (
                <div>
                  <div className="text-[11px] uppercase tracking-[0.18em] text-[hsl(var(--danger))] font-semibold mb-2 flex items-center gap-1.5">
                    <AlertTriangle className="w-3.5 h-3.5" /> Alertas críticos ({analysis.critical_alerts.length})
                  </div>
                  <div className="space-y-1.5">
                    {analysis.critical_alerts.map((r, i) => (
                      <div
                        key={i}
                        data-testid={`cp-analysis-alert-${i}`}
                        className="bg-[hsl(var(--warning-bg))]/50 border border-[#DC2626]/30 rounded-lg px-3 py-2 flex items-start gap-2"
                      >
                        <SeverityBadge severity={r.severity} />
                        <div className="flex-1">
                          <div className="font-semibold">{r.title}</div>
                          <div className="text-[13px] text-muted-foreground">{r.detail}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Gaps */}
              {analysis.gaps?.length > 0 && (
                <div>
                  <div className="text-[11px] uppercase tracking-[0.18em] text-[hsl(var(--warning))] font-semibold mb-2">
                    Lacunas por bloco ({analysis.gaps.length})
                  </div>
                  <div className="space-y-1.5">
                    {analysis.gaps.map((g, i) => (
                      <div
                        key={i}
                        data-testid={`cp-analysis-gap-${i}`}
                        className="bg-card border border-border rounded-lg px-3 py-2 flex items-start gap-2"
                      >
                        <SeverityBadge severity={g.severity} />
                        <div className="flex-1">
                          <div className="font-semibold">
                            {g.block_label || blockLabel(g.block)}
                          </div>
                          <div className="text-[13px] text-muted-foreground">{g.issue}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Coherence issues */}
              {analysis.coherence_issues?.length > 0 && (
                <div>
                  <div className="text-[11px] uppercase tracking-[0.18em] text-primary font-semibold mb-2 flex items-center gap-1.5">
                    <Link2 className="w-3.5 h-3.5" /> Incoerências entre blocos ({analysis.coherence_issues.length})
                  </div>
                  <div className="space-y-1.5">
                    {analysis.coherence_issues.map((ci, i) => (
                      <div
                        key={i}
                        data-testid={`cp-analysis-coherence-${i}`}
                        className="bg-[hsl(var(--info-bg))]/60 border border-primary/20 rounded-lg px-3 py-2 flex items-start gap-2"
                      >
                        <SeverityBadge severity={ci.severity} />
                        <div className="flex-1">
                          <div className="font-semibold text-[12px]">
                            {(ci.between || []).map(blockLabel).join(" ↔ ")}
                          </div>
                          <div className="text-[13px] text-muted-foreground">{ci.issue}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Suggestions per block */}
              {analysis.suggestions?.length > 0 && (
                <div>
                  <div className="flex items-center justify-between gap-3 mb-2 flex-wrap">
                    <div className="text-[11px] uppercase tracking-[0.18em] text-[hsl(var(--warning))] font-semibold">
                      Sugestões por bloco ({analysis.suggestions.length})
                    </div>
                    {canEdit && (
                    <button
                      type="button"
                      onClick={applyAll}
                      disabled={applyingAll || notApplied.length === 0 || isFrozen}
                      data-testid="cp-apply-all-btn"
                      className={`text-[10px] uppercase tracking-[0.14em] font-semibold px-3 py-1.5 rounded-full whitespace-nowrap transition-colors ${
                        notApplied.length === 0 || isFrozen
                          ? "bg-muted text-muted-foreground/70 cursor-not-allowed"
                          : "bg-primary hover:bg-primary text-primary-foreground"
                      }`}
                    >
                      {notApplied.length === 0
                        ? "Todas aplicadas ✓"
                        : applyingAll
                        ? "A aplicar…"
                        : `Aplicar todos (${notApplied.length})`}
                    </button>
                    )}
                  </div>
                  <div className="space-y-2">
                    {analysis.suggestions.map((s, i) => {
                      const key = s.field ? `${s.block}:${s.field}` : s.block;
                      const isApplied = !!applied[key];
                      const busy = applyingKey === key;
                      return (
                        <div
                          key={i}
                          data-testid={`cp-analysis-suggestion-${i}`}
                          className="bg-muted/40 border border-primary/40 rounded-lg px-3 py-2.5"
                        >
                          <div className="flex items-start gap-3">
                            <div className="flex-1 min-w-0">
                              <div className="text-[10px] uppercase tracking-[0.14em] text-[hsl(var(--warning))] font-semibold">
                                {s.block_label || blockLabel(s.block)}
                                {s.field && (
                                  <span className="ml-1 text-muted-foreground normal-case">
                                    · campo: {s.field}
                                  </span>
                                )}
                              </div>
                              <div className="text-[13px] mt-1 whitespace-pre-wrap">
                                {s.suggested_text}
                              </div>
                              {s.reason && (
                                <div className="text-[11px] text-muted-foreground italic mt-1">
                                  Motivo: {s.reason}
                                </div>
                              )}
                              {s.sources?.length > 0 && (
                                <div className="text-[11px] text-[hsl(var(--warning))] mt-1 flex items-center gap-1">
                                  <BookOpen className="w-3 h-3" />
                                  <span>{s.sources.join(" · ")}</span>
                                </div>
                              )}
                            </div>
                            {canEdit && (
                            <button
                              type="button"
                              onClick={() => applyOne(s)}
                              disabled={isApplied || busy || isFrozen}
                              data-testid={`cp-apply-btn-${i}`}
                              className={`text-[10px] uppercase tracking-[0.14em] font-semibold px-3 py-1.5 rounded-full whitespace-nowrap self-center transition-colors ${
                                isApplied
                                  ? "bg-[hsl(var(--success-bg))] text-[#059669] border border-[#059669]/30 cursor-default"
                                  : "bg-foreground hover:bg-foreground/90 text-background disabled:bg-[#94A3B8]"
                              }`}
                            >
                              {isApplied
                                ? "Aplicada ✓"
                                : busy
                                ? "A aplicar…"
                                : "Aplicar"}
                            </button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  <div className="text-[11px] text-muted-foreground mt-2 flex items-start gap-1">
                    <span>💡</span>
                    <span>
                      As sugestões são <b>anexadas</b> ao bloco (nunca substituem). Cada
                      aplicação cria automaticamente uma nova versão (V2, V3, …).
                    </span>
                  </div>
                </div>
              )}

              {/* Manual insights */}
              {analysis.manual_insights?.length > 0 && (
                <div>
                  <div className="text-[11px] uppercase tracking-[0.18em] text-[hsl(var(--warning))] font-semibold mb-2 flex items-center gap-1.5">
                    <BookOpen className="w-3.5 h-3.5" /> Insights do Manual do Cuidador
                  </div>
                  <div className="space-y-1.5">
                    {analysis.manual_insights.map((mi, i) => (
                      <div
                        key={i}
                        data-testid={`cp-analysis-manual-${i}`}
                        className="bg-card border border-border rounded-lg px-3 py-2"
                      >
                        <div className="font-semibold text-[13px]">{mi.topic}</div>
                        <div className="text-[13px] text-muted-foreground mt-0.5">{mi.insight}</div>
                        {mi.source && (
                          <div className="text-[11px] text-[hsl(var(--warning))] mt-1">
                            📖 {mi.source}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Recommended next actions */}
              {analysis.recommended_next_actions?.length > 0 && (
                <div className="bg-[hsl(var(--success-bg))] border border-[#059669]/30 rounded-lg p-3">
                  <div className="text-[11px] uppercase tracking-[0.16em] text-[#059669] font-semibold mb-2 flex items-center gap-1.5">
                    <CheckCircle2 className="w-3.5 h-3.5" /> Próximas acções recomendadas
                  </div>
                  <ul className="space-y-1 text-[13px]">
                    {analysis.recommended_next_actions.map((a, i) => (
                      <li key={i} className="flex items-start gap-1.5">
                        <TrendingUp className="w-3 h-3 text-[#059669] mt-1" />
                        <span>{a}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Applied history */}
              {analysis.applied_suggestions?.length > 0 && (
                <details className="text-[12px]">
                  <summary className="cursor-pointer text-muted-foreground hover:text-foreground py-1">
                    Histórico de sugestões aplicadas ({analysis.applied_suggestions.length})
                  </summary>
                  <div className="mt-1.5 space-y-1">
                    {analysis.applied_suggestions.map((rec, i) => (
                      <div
                        key={i}
                        className="flex items-center gap-2 py-1 border-b border-border/60 last:border-b-0"
                      >
                        <span className="text-[10px] uppercase tracking-widest text-[hsl(var(--warning))] font-semibold">
                          {blockLabel(rec.block)}
                          {rec.field && ` · ${rec.field}`}
                        </span>
                        <span className="text-muted-foreground flex-1 truncate">{rec.text}</span>
                        <span className="text-[10px] text-muted-foreground whitespace-nowrap">
                          {(rec.applied_at || "").slice(0, 10)}
                        </span>
                      </div>
                    ))}
                  </div>
                </details>
              )}

              {/* Meta */}
              <div className="text-[10px] text-muted-foreground pt-2 border-t border-border/60 flex items-center justify-between">
                <span>
                  Análise gerada em {(analysis.generated_at || "").slice(0, 16).replace("T", " ")}
                  {analysis.generated_by ? ` por ${analysis.generated_by}` : ""}
                </span>
                {analysis.version_label && (
                  <span>Versão base: {analysis.version_label}</span>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
